import { useEffect, useState } from 'react';

const RECAPTCHA_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';
const SCRIPT_ID = 'recaptcha-api-script';
const READY_POLL_MS = 50;
const READY_TIMEOUT_MS = 10000;
// Cadence after the timeout: still recovers if the script lands late, without
// holding a 20Hz timer open forever when reCAPTCHA is blocked outright.
const SLOW_POLL_MS = 1000;

function isRecaptchaReady() {
  return Boolean(globalThis.grecaptcha?.render);
}

/**
 * Loads Google's reCAPTCHA script on demand, from the pages that actually have
 * a form.
 *
 * It used to load site-wide from a tag in index.html, which meant every visitor
 * — including people only reading the pricing page — opened a connection to
 * Google. reCAPTCHA remains exempt from consent as an anti-fraud measure, and
 * that claim is materially stronger when the script loads on the form rather
 * than on every page.
 *
 * The script is injected at most once per page load and shared by every caller;
 * a second form mounting simply resolves against the already-loaded copy.
 *
 * SRI is deliberately not applied — Google's loader is a dynamic, unversioned
 * URL whose bytes change without notice and for which no integrity hash is
 * published. This mirrors the accepted residual risk previously documented in
 * index.html (ZAP pluginId 90003).
 */
export function useRecaptchaLoader() {
  const [isLoaded, setIsLoaded] = useState(isRecaptchaReady);

  useEffect(() => {
    if (isLoaded) {
      return undefined;
    }

    let cancelled = false;

    // `grecaptcha.render` is not necessarily present the instant the script's
    // load event fires, so readiness is resolved by polling rather than by
    // trusting the event. Polling from a timer also keeps setState out of the
    // effect body, which would otherwise cascade renders.
    const started = Date.now();
    let timer;
    let warned = false;

    // Polling continues past READY_TIMEOUT_MS instead of stopping. Giving up
    // used to be permanent — the interval was cleared and the effect only
    // re-runs on `isLoaded`, which stays false — so a script that arrived at
    // 11s left the form with no captcha until a manual refresh. The timeout now
    // only downgrades the cadence and warns once, so a slow network recovers.
    const tick = () => {
      if (cancelled) {
        return;
      }
      if (isRecaptchaReady()) {
        setIsLoaded(true);
        return;
      }

      const timedOut = Date.now() - started > READY_TIMEOUT_MS;
      if (timedOut && !warned) {
        warned = true;
        console.warn('reCAPTCHA did not become ready within 10s; still waiting');
      }

      timer = setTimeout(tick, timedOut ? SLOW_POLL_MS : READY_POLL_MS);
    };

    timer = setTimeout(tick, READY_POLL_MS);

    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = RECAPTCHA_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isLoaded]);

  return { isLoaded };
}
